import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { startOfDay } from 'date-fns';
import Text from '@/components/forms/text';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Button } from '@/components/ui/button';
import { useChangeContact } from '@/hooks/api/use-amendments';
import { useContractsData } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { toDate } from '@/lib/dates';
import { hasContractApporteur43397 as hasApporteur43397 } from '@/lib/contracts';
import { blockingPhoneSchema, type BlockingPhoneValues } from '@/schemas/blocking-phone';
import { hasSelfServiceBrokerContract } from '@/components/blocking-phone/brokers';
import { MFA_ACTIVATION_DATE } from '@/components/blocking-phone/constants';
import BrokerMfaPopup, { type BrokerApporteur } from '@/components/blocking-phone/broker-mfa-popup';

// Une fois le numéro validé, on ne re-bloque pas le client pendant le délai de
// traitement de l'avenant (numéro « mis à jour sous 24h » après clic sur le
// lien de validation reçu par email). Au-delà, si le numéro est toujours absent
// du dossier, la popin réapparaît pour relancer le client.
const STORAGE_KEY = 'perso-v3-blocking-phone-validated';
const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

const hasValidDismiss = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < FORTY_EIGHT_HOURS_MS;
};

const BlockingPhonePopup = () => {
  const changeContact = useChangeContact();
  const user = useCurrentUser();
  const phone = user.address_phone;
  const contracts = useContractsData();
  const hasContractApporteur43397 = hasApporteur43397(contracts);
  // Coordonnées du courtier : contracts.data[n].apporteurs.apporteur1
  // (raisonSociale, phone, email_principal…) — il n'y a pas d'apporteur
  // sur l'objet user.
  const apporteurContrat = (contracts[contracts.length - 1]?.apporteurs?.apporteur1 || null) as BrokerApporteur | null;
  const idUser = user.belair_id;
  const email = user.email;
  // Accès défensif : la popin est montée dès Page, possiblement avant que les
  // liens ne soient chargés — on évite le crash du selector non gardé.
  const loading = changeContact.isPending;

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<BlockingPhoneValues>({
    defaultValues: { tel_mobile: '', password: '' },
    resolver: zodResolver(blockingPhoneSchema) as unknown as Resolver<BlockingPhoneValues>,
  });

  // On n'évalue la popin qu'une fois le client connecté ET ses données chargées
  // (idUser présent) : avant cela on ne sait pas s'il a renseigné son téléphone,
  // et l'afficher serait prématuré.
  const isUserLoaded = idUser !== undefined && idUser !== null && idUser !== '';
  const hasPhone = typeof phone === 'string' && phone.trim() !== '';

  // Le statut direct/courtier n'est fiable qu'une fois les contrats chargés :
  // avant ça, hasContractApporteur43397 vaut false même pour un client direct,
  // et on afficherait la mauvaise popin le temps du fetch.
  const contractsLoaded = Array.isArray(contracts) && contracts.length > 0;
  // Client courtier « self-service » (LSA, RVA, AssurPeople, Les Furets) :
  // même popin bloquante de saisie que le direct. Les autres courtiers
  // reçoivent la popin bloquante MFA « contactez votre courtier ».
  const isBrokerClient = contractsLoaded && !hasContractApporteur43397;
  const showSelfServicePopin = contractsLoaded
    && (hasContractApporteur43397 || hasSelfServiceBrokerContract(contracts));

  const open = isUserLoaded && showSelfServicePopin && !hasPhone && !hasValidDismiss();

  // Popin courtier bloquante (non fermable), à partir de l'activation de la
  // MFA et tant que le numéro est absent du dossier : le client courtier ne
  // peut pas le saisir lui-même, il doit contacter son courtier.
  const isMfaActive = startOfDay(new Date()).getTime() >= startOfDay(toDate(MFA_ACTIVATION_DATE)).getTime();
  const openBroker = isUserLoaded && isBrokerClient && !showSelfServicePopin
    && isMfaActive && !hasPhone;

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (openBroker) {
    return <BrokerMfaPopup apporteur={apporteurContrat} />;
  }

  if (!open) return null;

  const onSubmit = (values: BlockingPhoneValues) => {
    if (!isUserLoaded) return;
    changeContact.mutate({
      tiersId: idUser as string | number,
      form: {
        tiers: idUser,
        tel_mobile: values.tel_mobile,
        email,
        email_confirmation: email,
        password: values.password,
      },
      successFunction: () => {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        reset();
      },
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-12 bg-[rgba(0,30,49,0.5)] backdrop-blur-[16px]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="blocking-phone-title"
      aria-describedby="blocking-phone-desc"
    >
      <div className="relative w-full max-w-[450px] bg-card rounded-[18px] shadow-[0_24px_60px_rgba(0,40,65,0.4)] px-7 pt-8 pb-6 text-center animate-[slideUp_0.25s_ease]">
        <div
          className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(248,170,54,0.15)' }}
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M6.5 3h3l1.5 4-2 1.5a13 13 0 006 6l1.5-2 4 1.5v3a2 2 0 01-2 2A16 16 0 014.5 5a2 2 0 012-2z" fill="#F8AA36" />
            <circle cx="18.5" cy="5.5" r="4" fill="#fff" stroke="#E08A0C" strokeWidth="1.4" />
            <path d="M18.5 4v2M18.5 7.4v.1" stroke="#E08A0C" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </div>

        <h2 id="blocking-phone-title" className="text-xl font-semibold text-primary leading-[1.3] mb-3.5">
          Une information nous manque
        </h2>
        <p id="blocking-phone-desc" className="text-sm text-muted-foreground leading-[1.65] mb-2">
          Il nous manque des coordonnées pour valider la création de votre espace assuré
          de façon sécurisée, en particulier votre{' '}
          <strong className="text-foreground font-semibold">numéro de téléphone</strong>,
          actuellement absent de votre dossier.
        </p>
        <p className="text-sm text-muted-foreground leading-[1.65] mb-2">
          Pour activer votre espace, renseignez votre numéro de téléphone pour valider la
          double authentification&nbsp;:
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="text-left mt-12 space-y-4">
          <Text
            type="text"
            autoComplete="tel"
            label="Numéro de téléphone"
            {...register('tel_mobile')}
            value={watch('tel_mobile')}
            error={errors.tel_mobile?.message}
            required
          />
          <Text
            type="password"
            autoComplete="current-password"
            label="Votre mot de passe"
            {...register('password')}
            value={watch('password')}
            error={errors.password?.message}
            required
          />

          {loading ? (
            <div className="flex justify-center py-2">
              <SmallSpinner />
            </div>
          ) : (
            <Button type="submit" className="w-full font-semibold text-[15px] py-3.5">
              Je valide mon numéro de téléphone
            </Button>
          )}
        </form>

      </div>
    </div>,
    document.body,
  );
};

export default BlockingPhonePopup;
